import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { PendingButton } from '@/components/ui/pending-button';
import {
  importBatchRefusal,
  useUndoImport,
  useUndoPreview,
} from '@/hooks/queries/useFinanceImportBatches';
import { answered } from '@/lib/reportsRead';
import { bankUnreadyMessage } from '@/lib/bankReads';
import { IMPORT_CHANGED_ELSEWHERE } from './importCopy';

/**
 * Workstream C — confirm/preview modal for undoing an import batch.
 *
 * The undo engine (service, "no actions taken" safety guard, and the
 * `useUndoPreview` / `useUndoImport` hooks) is already built. This component is
 * only the UI: it dry-runs the preview, summarizes what the cascade would
 * permanently delete (ADR-016: Undo Import is destructive), and gates the real
 * undo behind an explicit confirm.
 *
 * Blocking-reason copy is plain English by the time it reaches us: the server
 * translates every enum (LedgerStatus / BankTransactionAttribution) through
 * `FinanceEnumLabels` at the single `EvaluateUndoSafetyAsync` site shared by the
 * preview and the undo paths, so "Excluded from report" — not the raw
 * `ExcludedFromReport` enum — is what the wire carries, and Preview/Undo always
 * agree. We render the strings verbatim (no second translation needed) and never
 * fabricate our own. See `project_undo_blocking_reason_enum_leak`.
 */
interface UndoImportDialogProps {
  /** Batch to undo. Drives the preview fetch while the dialog is open. */
  batchId: string;
  /** Original file name, echoed back so the treasurer knows which import. */
  originalFileName: string;
  /** The batch's token from the history row; the undo sends it back. */
  concurrencyVersion: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful undo so the parent can refresh / close. */
  onUndone: () => void;
}

/** Plain-English count phrase, singular/plural aware. Returns null at zero. */
function countPhrase(count: number, singular: string, plural: string): string | null {
  if (count <= 0) return null;
  return `${count} ${count === 1 ? singular : plural}`;
}

export function UndoImportDialog({
  batchId,
  originalFileName,
  concurrencyVersion,
  open,
  onOpenChange,
  onUndone,
}: UndoImportDialogProps) {
  // Only fetch the preview while the dialog is open (null batchId disables the
  // query — see useUndoPreview's `enabled`).
  const previewRead = useUndoPreview(open ? batchId : null);
  const preview = answered(previewRead);
  const previewUnready = bankUnreadyMessage('what this import added', previewRead);
  const { undoImport, isPending } = useUndoImport();

  // Reasons surfaced by the server on a 409 (the preview said "clean" but a
  // downstream action landed between preview and confirm). Cleared on each
  // attempt so a retry doesn't show stale reasons.
  const [conflictReasons, setConflictReasons] = useState<string[] | null>(null);
  // A stale token is not a reason the import can't be undone: the history has
  // reloaded with the fresh token, so the treasurer can look again and retry.
  const [changedElsewhere, setChangedElsewhere] = useState(false);

  const blockingReasons = conflictReasons ?? preview?.blockingReasons ?? [];
  const blocked = !preview || preview.canUndo === false || blockingReasons.length > 0;

  const onConfirm = async () => {
    setConflictReasons(null);
    setChangedElsewhere(false);
    try {
      await undoImport({ row: { id: batchId, concurrencyVersion }, reason: null });
      onUndone();
    } catch (e) {
      const refusal = importBatchRefusal(e);
      if (refusal.kind === 'undoBlocked' && refusal.reasons.length > 0) {
        setConflictReasons(refusal.reasons);
      } else if (refusal.kind === 'changedElsewhere') {
        setChangedElsewhere(true);
      } else {
        setConflictReasons(['Something went wrong undoing this import. Please try again.']);
      }
    }
  };

  const parts =
    preview != null
      ? [
          countPhrase(preview.contributionCount, 'contribution', 'contributions'),
          countPhrase(preview.bankTransactionCount, 'bank line', 'bank lines'),
          countPhrase(preview.expenditureCount, 'expense', 'expenses'),
        ].filter((p): p is string => p != null)
      : [];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Undo this import?</AlertDialogTitle>
          <AlertDialogDescription>
            {previewUnready ? (
              <span role="alert">{previewUnready}</span>
            ) : preview == null ? (
              <span className="flex items-center gap-2">
                <InlineSpinner decorative />
                Checking what this import added…
              </span>
            ) : (
              <>
                This will permanently delete{' '}
                {parts.length > 0 ? <strong>{parts.join(' and ')}</strong> : 'this import'} from{' '}
                <strong>{originalFileName}</strong>. This can&rsquo;t be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {blockingReasons.length > 0 && (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <p className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              This import can&rsquo;t be undone:
            </p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
              {blockingReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
            <p className="mt-2 text-amber-800">
              Reopen or void those rows first, then try again.
            </p>
          </div>
        )}

        {changedElsewhere && (
          <p role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {IMPORT_CHANGED_ELSEWHERE}
          </p>
        )}

        <AlertDialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Keep this import
          </Button>
          <PendingButton
            type="button"
            variant="destructive"
            pending={isPending}
            onClick={onConfirm}
            disabled={blocked}
          >
            {isPending ? 'Deleting…' : 'Delete these rows'}
          </PendingButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
